import React, { ChangeEvent, FormEvent, useRef, useState } from "react";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import {
  useFetchProjectImagesQuery,
  useUploadProjectImageMutation,
  useDeleteProjectImageMutation,
} from "../../store/api/projectImagesApi";
import { getApiErrorMessage } from "../../store/api/baseApi";
import "./admin.css";

const CATEGORIES = ["All", "Residential", "Commercial", "Industrial"];
const UPLOAD_CATEGORIES = ["Residential", "Commercial", "Industrial"];

const initialForm = {
  title: "",
  category: "Residential",
};

const ProjectImageManager = () => {
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>(undefined);
  const [form, setForm] = useState(initialForm);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadError, setUploadError] = useState("");
  const [uploadSuccess, setUploadSuccess] = useState("");
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const {
    data: images = [],
    isLoading,
    error: fetchError,
  } = useFetchProjectImagesQuery(
    categoryFilter ? { category: categoryFilter } : undefined
  );

  const [uploadImage, { isLoading: isUploading }] = useUploadProjectImageMutation();
  const [deleteImage, { isLoading: isDeleting }] = useDeleteProjectImageMutation();

  const fetchErrorMessage = fetchError
    ? getApiErrorMessage(fetchError as FetchBaseQueryError)
    : "";

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectedFiles(Array.from(event.target.files ?? []));
    setUploadError("");
    setUploadSuccess("");
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setUploadError("");
    setUploadSuccess("");

    if (selectedFiles.length === 0) {
      setUploadError("Please select at least one image file to upload.");
      return;
    }

    try {
      const uploads = selectedFiles.map((file, index) =>
        uploadImage({
          file,
          title: selectedFiles.length > 1
            ? `${form.title || file.name.replace(/\.[^/.]+$/, "")} ${index + 1}`
            : form.title || file.name.replace(/\.[^/.]+$/, ""),
          category: form.category,
        }).unwrap()
      );

      await Promise.all(uploads);
      setUploadSuccess(`${selectedFiles.length} image${selectedFiles.length > 1 ? "s" : ""} uploaded successfully.`);
      setForm(initialForm);
      setSelectedFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
      window.setTimeout(() => setUploadSuccess(""), 3000);
    } catch (err) {
      setUploadError(getApiErrorMessage(err as FetchBaseQueryError));
    }
  };

  const handleRemove = async (id: string) => {
    try {
      await deleteImage(id).unwrap();
    } catch (err) {
      setUploadError(getApiErrorMessage(err as FetchBaseQueryError));
    }
  };

  return (
    <section className="admin-page">
      <div className="container-fluid px-0">
        <div className="row g-4">
          {/* Upload Form */}
          <div className="col-12">
            <div style={{ background: "#fff", borderRadius: 14, border: "1.5px solid #e2e8f0", overflow: "hidden", boxShadow: "0 2px 12px #0000000a" }}>
              {/* Gradient header */}
              <div style={{ background: "linear-gradient(135deg, #134e4a, #0d9488, #14b8a6)", padding: "10px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 30, height: 30, borderRadius: 8, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <i className="bi bi-images" style={{ color: "#fff", fontSize: 14 }} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: "0.9rem", color: "#fff", lineHeight: 1.2 }}>Project Image Manager</div>
                    <div style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.75)" }}>Upload single or multiple images to the project gallery</div>
                  </div>
                </div>
                <span style={{ background: "rgba(255,255,255,0.2)", color: "#fff", border: "1px solid rgba(255,255,255,0.35)", borderRadius: 20, padding: "3px 12px", fontSize: "0.74rem", fontWeight: 600 }}>
                  {isLoading ? "…" : images.length} Images
                </span>
              </div>
              <div className="card-body p-4">

                {uploadError && (
                  <div className="alert alert-danger py-2 mb-3">{uploadError}</div>
                )}
                {uploadSuccess && (
                  <div className="alert alert-success py-2 mb-3">{uploadSuccess}</div>
                )}

                <form onSubmit={handleSubmit}>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label htmlFor="imageTitle" className="form-label fw-semibold">
                        Image Title
                        <small className="d-block fw-normal text-muted" style={{fontSize:'0.71rem'}}>Batch title for all uploads; defaults to filename if left blank</small>
                      </label>
                      <input
                        id="imageTitle"
                        className="form-control"
                        title="Image title"
                        value={form.title}
                        onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                        disabled={isUploading}
                      />
                    </div>

                    <div className="col-md-6">
                      <label htmlFor="imageCategory" className="form-label fw-semibold">
                        Category
                      </label>
                      <select
                        id="imageCategory"
                        className="form-select"
                        value={form.category}
                        onChange={(e) => setForm((prev) => ({ ...prev, category: e.target.value }))}
                        disabled={isUploading}
                      >
                        {UPLOAD_CATEGORIES.map((cat) => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>

                    <div className="col-12">
                      <label htmlFor="imageFiles" className="form-label fw-semibold">
                        Select Images <span className="text-muted fw-normal">(JPEG / PNG, max 20 MB each)</span>
                      </label>
                      <input
                        id="imageFiles"
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept="image/jpeg,image/png"
                        className="form-control"
                        onChange={handleFileChange}
                        disabled={isUploading}
                      />
                      {selectedFiles.length > 0 && (
                        <div className="d-flex flex-wrap gap-2 mt-2">
                          {selectedFiles.map((file) => (
                            <span
                              key={`${file.name}-${file.size}`}
                              className="badge text-bg-secondary rounded-pill px-3 py-2"
                            >
                              {file.name}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="col-12">
                      <button
                        type="submit"
                        className="btn btn-primary btn-sm d-inline-flex align-items-center gap-2"
                        disabled={isUploading || selectedFiles.length === 0}
                      >
                        {isUploading ? (
                          <>
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                            Uploading…
                          </>
                        ) : (
                          <>
                            <i className="bi bi-cloud-upload" />
                            Upload Image{selectedFiles.length > 1 ? "s" : ""}
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* Gallery Table */}
          <div className="col-12">
            <div style={{ background: "#fff", borderRadius: 14, border: "1.5px solid #e2e8f0", overflow: "hidden", boxShadow: "0 2px 12px #0000000a" }}>
              <div style={{ background: "linear-gradient(135deg, #1e3a5f, #1d4ed8, #3b82f6)", padding: "10px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 30, height: 30, borderRadius: 8, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <i className="bi bi-grid-3x3-gap-fill" style={{ color: "#fff", fontSize: 14 }} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: "0.9rem", color: "#fff", lineHeight: 1.2 }}>Current Gallery</div>
                    <div style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.75)" }}>Preview and manage project images</div>
                  </div>
                </div>
              </div>
              <div className="card-body p-0">
                <div className="d-flex justify-content-end align-items-center flex-wrap gap-3 p-3 border-bottom">
                  <div style={{ minWidth: "200px" }}>
                    <select
                      className="form-select form-select-sm"
                      value={categoryFilter ?? ""}
                      onChange={(e) => setCategoryFilter(e.target.value || undefined)}
                      aria-label="Filter by category"
                      title="Filter by category"
                    >
                      {CATEGORIES.map((cat) => (
                        <option key={cat} value={cat === "All" ? "" : cat}>
                          {cat}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {fetchErrorMessage && (
                  <div className="alert alert-danger m-3">{fetchErrorMessage}</div>
                )}

                {isLoading ? (
                  <div className="alert alert-light border m-3">Loading images…</div>
                ) : images.length === 0 ? (
                  <div className="alert alert-light border m-3">
                    No images found{categoryFilter ? ` in "${categoryFilter}"` : ""}. Upload one above.
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Preview</th>
                          <th>Title</th>
                          <th>Category</th>
                          <th>Description</th>
                          <th>Uploaded</th>
                          <th className="text-end">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {images.map((image) => (
                          <tr key={image.id}>
                            <td style={{ width: 140 }}>
                              <img
                                src={image.imageUrl}
                                alt={image.title}
                                className="img-fluid rounded-3"
                                style={{ width: 120, height: 80, objectFit: "cover" }}
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src =
                                    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='80'%3E%3Crect width='120' height='80' fill='%23e9ecef'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%236c757d' font-size='11'%3ENo image%3C/text%3E%3C/svg%3E";
                                }}
                              />
                            </td>
                            <td>
                              <strong>{image.title || "—"}</strong>
                              <div className="small text-muted">{image.id}</div>
                            </td>
                            <td>
                              <span className="badge text-bg-primary rounded-pill px-3 py-2">
                                {image.category}
                              </span>
                            </td>
                            <td className="text-muted">{image.description || "—"}</td>
                            <td className="text-muted small">{image.createdAt || "—"}</td>
                            <td className="text-end">
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm admin-icon-btn"
                                onClick={() => handleRemove(image.id)}
                                disabled={isDeleting}
                                aria-label={`Delete ${image.title}`}
                                title="Delete image"
                              >
                                <i className="bi bi-trash" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProjectImageManager;
